/**
 * Settori Slider Component
 * Manages Swiper slider for category links (settori)
 */
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

class SettoriSlider {
    constructor(element) {
        this.element = element;
        this.swiper = null;
        this.init();
    }

    /**
     * Initialize the slider
     */
    init() {
        if (!this.element) {
            console.warn('SettoriSlider: Element not found');
            return;
        }

        const swiperContainer = this.element.querySelector('.settori-swiper');

        if (!swiperContainer) {
            console.warn('SettoriSlider: Swiper container not found');
            return;
        }

        // Verifica che ci siano slide
        const slides = swiperContainer.querySelectorAll('.swiper-slide');
        if (slides.length === 0) {
            console.warn('SettoriSlider: No slides found');
            return;
        }

        this.initSwiper(swiperContainer);
    }

    /**
     * Initialize Swiper instance
     */
    initSwiper(container) {
        // Wait a bit to ensure DOM is fully ready (Visual Composer might render async)
        const initSwiperDelayed = () => {
            // Find navigation buttons - they should be siblings of swiper-wrapper, inside container
            let nextButton = container.querySelector('.swiper-button-next');
            let prevButton = container.querySelector('.swiper-button-prev');

            // If not found, try parent container
            if (!nextButton || !prevButton) {
                const parentContainer = container.parentElement;
                if (parentContainer) {
                    if (!nextButton) {
                        nextButton = parentContainer.querySelector('.swiper-button-next');
                    }
                    if (!prevButton) {
                        prevButton = parentContainer.querySelector('.swiper-button-prev');
                    }
                }
            }

            // Check if we have enough slides
            const slides = container.querySelectorAll('.swiper-slide');
            const slidesCount = slides.length;

            if (slidesCount === 0) {
                console.warn('SettoriSlider: No slides found in container');
                return;
            }

            // Build navigation config only if buttons exist
            const navigationConfig = {};
            if (nextButton && prevButton) {
                navigationConfig.nextEl = nextButton;
                navigationConfig.prevEl = prevButton;
            } else {
                console.warn('SettoriSlider: Navigation buttons not found', {
                    hasNext: !!nextButton,
                    hasPrev: !!prevButton,
                    containerClass: container.className
                });
            }

            try {
                this.swiper = new Swiper(container, {
                    modules: [Navigation],
                    slidesPerView: 1,
                    spaceBetween: 16,
                    loop: false,
                    watchOverflow: true,
                    navigation: Object.keys(navigationConfig).length > 0 ? navigationConfig : false,
                    breakpoints: {
                        // Mobile: 1 slide
                        320: {
                            slidesPerView: 1,
                            spaceBetween: 16,
                        },
                        // Tablet: 2-3 slides
                        576: {
                            slidesPerView: 2,
                            spaceBetween: 16,
                        },
                        768: {
                            slidesPerView: 3,
                            spaceBetween: 16,
                        },
                        // Desktop: 6 slides
                        992: {
                            slidesPerView: 4,
                            spaceBetween: 16,
                        },
                        1200: {
                            slidesPerView: 5,
                            spaceBetween: 16,
                        },
                        1400: {
                            slidesPerView: 6,
                            spaceBetween: 16,
                        }
                    },
                    on: {
                        init: (swiper) => {
                            console.log('SettoriSlider: Swiper initialized successfully', {
                                slides: swiper.slides.length,
                                hasNavigation: !!swiper.params.navigation
                            });
                        },
                        slideChange: (swiper) => {
                            // Update button states
                            if (nextButton && prevButton) {
                                if (swiper.isBeginning) {
                                    prevButton.classList.add('swiper-button-disabled');
                                } else {
                                    prevButton.classList.remove('swiper-button-disabled');
                                }
                                if (swiper.isEnd) {
                                    nextButton.classList.add('swiper-button-disabled');
                                } else {
                                    nextButton.classList.remove('swiper-button-disabled');
                                }
                            }
                        }
                    }
                });

                // Verify Swiper was created
                if (!this.swiper) {
                    console.error('SettoriSlider: Failed to initialize Swiper');
                    return;
                }

                // Add click handlers as fallback if Swiper navigation doesn't work
                if (nextButton && prevButton) {
                    nextButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.swiper && !this.swiper.isEnd) {
                            this.swiper.slideNext();
                        }
                    });

                    prevButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.swiper && !this.swiper.isBeginning) {
                            this.swiper.slidePrev();
                        }
                    });
                }
            } catch (error) {
                console.error('SettoriSlider: Error initializing Swiper', error);
            }
        };

        // Try immediate initialization
        initSwiperDelayed();

        // Also try after a short delay in case DOM isn't ready
        setTimeout(initSwiperDelayed, 100);
    }

    /**
     * Destroy the slider
     */
    destroy() {
        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
    }

    /**
     * Update the slider (useful for dynamic content)
     */
    update() {
        if (this.swiper) {
            this.swiper.update();
        }
    }
}

/**
 * Initialize Settori Sliders
 */
function initSettoriSliders() {
    const settoriSliderElements = document.querySelectorAll('.settori-slider-container');

    if (settoriSliderElements.length === 0) {
        return; // Exit if no settori slider elements found
    }

    console.log(`SettoriSlider: Found ${settoriSliderElements.length} slider(s) to initialize`);

    // Initialize each settori slider (only if not already initialized)
    settoriSliderElements.forEach((element, index) => {
        if (!element.dataset.settoriInitialized) {
            element.dataset.settoriInitialized = 'true';
            console.log(`SettoriSlider: Initializing slider ${index + 1}`);
            new SettoriSlider(element);
        }
    });
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', initSettoriSliders);

// Also initialize if called after DOM is loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSettoriSliders);
} else {
    // DOM is already loaded
    initSettoriSliders();
}

// Use MutationObserver to watch for dynamically added content (Visual Composer)
if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) { // Element node
                    // Check if the added node is a settori slider container
                    if (node.classList && node.classList.contains('settori-slider-container')) {
                        if (!node.dataset.settoriInitialized) {
                            console.log('SettoriSlider: Found dynamically added slider, initializing...');
                            initSettoriSliders();
                        }
                    }
                    // Check if any settori slider containers were added inside this node
                    const sliders = node.querySelectorAll && node.querySelectorAll('.settori-slider-container');
                    if (sliders && sliders.length > 0) {
                        sliders.forEach((slider) => {
                            if (!slider.dataset.settoriInitialized) {
                                console.log('SettoriSlider: Found dynamically added slider inside node, initializing...');
                                initSettoriSliders();
                            }
                        });
                    }
                }
            });
        });
    });

    // Start observing when DOM is ready
    if (document.body) {
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    } else {
        document.addEventListener('DOMContentLoaded', () => {
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        });
    }
}

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
    module.exports = SettoriSlider;
} else if (typeof window !== 'undefined') {
    window.SettoriSlider = SettoriSlider;
}
